import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";
import { employerAPI } from "../services/api";

/**
 * Post-submission confirmation for /employers/intake.
 * Renders the service reference from ?ref= and optionally verifies it
 * against the backend so a mistyped URL falls back gracefully.
 */
export default function EmployerIntakeSuccessPage() {
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const [status, setStatus] = useState(ref ? "loading" : "no_ref");

  useEffect(() => {
    if (!ref) return;
    let cancelled = false;
    (async () => {
      try {
        await employerAPI.getInquiryByRef(ref);
        if (!cancelled) setStatus("ok");
      } catch (err) {
        if (!cancelled) setStatus(err?.response?.status === 404 ? "not_found" : "ok");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ref]);

  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(ref);
      toast.success("Reference copied to clipboard");
    } catch {
      toast.error("Couldn't copy — please copy manually.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="employer-intake-success-page">
      <SiteHeader />
      <main className="py-16 lg:py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="shadow-lg border-blue-100">
            <CardContent className="p-8 lg:p-10 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-3">
                Thank you — we&apos;ve received your inquiry.
              </h1>
              {status === "not_found" ? (
                <p className="text-gray-600 mb-6">
                  We couldn&apos;t verify that reference. If you just submitted the
                  form, please refresh in a moment. If the problem persists, please
                  contact us and share the reference from your confirmation email.
                </p>
              ) : (
                <>
                  <p className="text-gray-600 mb-6">Your service reference is:</p>
                  {ref && (
                    <div
                      className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-5 py-3 mb-6"
                      data-testid="employer-intake-success-reference"
                    >
                      <span className="font-mono text-xl text-blue-800 tracking-wide">
                        {ref}
                      </span>
                      <button
                        onClick={copyRef}
                        className="ml-2 text-blue-600 hover:text-blue-800 transition-colors"
                        aria-label="Copy reference"
                        data-testid="employer-intake-success-copy"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  <p className="text-gray-700 leading-relaxed mb-8">
                    Keep this reference for your records. A member of the Inter-View
                    team will follow up{" "}
                    <span className="font-medium">within 2 business days</span>.
                  </p>
                </>
              )}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/">
                  <Button variant="outline" data-testid="employer-intake-success-home-btn">
                    Return to Inter-View home
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
